import React, { useCallback, useMemo, useState } from 'react';
import { Radio } from 'antd';
import { Rule } from 'antd/lib/form';
import { useTranslation } from 'react-i18next';
import FormInput from 'components/Input/FormInput';
import Text from 'components/Text/Text';
import classNames from 'classnames';

export const CHARITY_AMOUNT_FIELD_NAMES = ['charityAmount0', 'charityAmount1', 'charityAmount2', 'charityAmount3'];

const isFilled = (amount: unknown): amount is number => typeof amount === 'number';

interface IProps {
	setCharityAmount: (value: null | number) => void;
	setDefaultCharityAmountIndex: (value: number) => void;
	charityAmountIndex: 0 | 1 | 2 | 3;
	charityAmount: null | number;
	defaultCharityAmountIndex: Nullable<number>;
	isCharityEnabled: boolean;
}

export const CharityAmountField: React.FC<IProps> = React.memo(
	({
		setCharityAmount,
		setDefaultCharityAmountIndex,
		charityAmountIndex,
		charityAmount,
		defaultCharityAmountIndex,
		isCharityEnabled,
	}) => {
		const [hasError, setHasError] = useState(false);
		const { t } = useTranslation('error');
		const fieldName = `charityAmount${charityAmountIndex}`;
		const otherFieldNames = useMemo(
			() => CHARITY_AMOUNT_FIELD_NAMES.filter((name) => name !== fieldName),
			[fieldName],
		);
		const radioClasses = classNames('absolute transition duration-300 ease-in-out', {
			'bottom-0': !hasError,
			'-bottom-4.5': hasError,
		});

		const validationRules = useMemo<Rule[]>(
			() => [
				({ getFieldsValue }) => ({
					validator(_, value: Nullable<number> | undefined) {
						const otherAmounts = Object.values(getFieldsValue(otherFieldNames)).filter(isFilled);
						const isFirstAmount = charityAmountIndex === 0;
						let errorMessage: Nullable<string> = null;

						if (!isFilled(value)) {
							if (isFirstAmount && isCharityEnabled && otherAmounts.length === 0) {
								errorMessage = t('enterAtLeastOneCharityAmount');
							}
						} else if (value <= 0) {
							errorMessage = t('charityAmountMustBeGreaterThanZero');
						} else if (otherAmounts.includes(value)) {
							errorMessage = t('thisValueAlreadyExistsInAnotherCharityAmount');
						}

						setHasError(errorMessage !== null);
						return errorMessage ? Promise.reject(new Error(errorMessage)) : Promise.resolve();
					},
				}),
			],
			[t, otherFieldNames, charityAmountIndex, isCharityEnabled],
		);

		const handleRadioClick = useCallback(() => {
			setDefaultCharityAmountIndex(charityAmountIndex);
		}, [setDefaultCharityAmountIndex, charityAmountIndex]);

		return (
			<div className="relative flex flex-1 flex-col">
				<FormInput
					testId={`CharityAmount${charityAmountIndex}Field`}
					type="number"
					name={fieldName}
					label={
						defaultCharityAmountIndex === charityAmountIndex
							? `charityAmount${charityAmountIndex}DefaultLabel`
							: `charityAmount${charityAmountIndex}Label`
					}
					onChangeNumber={setCharityAmount}
					min={0}
					precision={2}
					validationRules={validationRules}
					dependencies={otherFieldNames}
					disabled={!isCharityEnabled}
				/>
				<Radio
					className={radioClasses}
					value={charityAmountIndex}
					onClick={handleRadioClick}
					disabled={!isCharityEnabled || charityAmount === null}>
					<Text translationNameSpace="form" translationKey="useByDefault" asFragment={true} />
				</Radio>
			</div>
		);
	},
);
